import { __decorate } from "tslib";
import { mixins } from 'vue-class-component';
import { Component } from 'vue-property-decorator';
import PageTitle from '@/components/v2/PageTitle.vue';
import ProductForm from '@/components/v2/ecommerce/ProductForm.vue';
import NewProductForm from '@/components/v2/ecommerce/ProductFormEcommerce.vue';
import ProductMixin from '@/helpers/vue_mixins/v2/ecommerce/ProductMixin';
import ProductImageUploadMixin from '@/helpers/vue_mixins/v2/ecommerce/ProductImageUploadMixin';
import ProductCreate from '@/plugins/graphql/mutations/v2/ecommerce/product-create.gql';
import Freebies from '@/plugins/graphql/queries/v2/ecommerce/freebies.gql';
import Products from '@/plugins/graphql/queries/v2/ecommerce/products.gql';
import ProductVariantsMixin from '@/helpers/vue_mixins/v2/ecommerce/ProductVariantsMixin';
import { successfulCreate } from '@/helpers/mixins/LocalizationHelper';
import { ProductCategory } from '@/helpers/entities/enums/v2/ProductCategory';
// import { EcommerceProductAddOnInputImpl } from '@/helpers/entities/interfaces/v2/model_implementations/EcommerceProductAddOnInputImpl';
import ProductAddOnMixin from '@/helpers/vue_mixins/v2/ecommerce/ProductAddOnMixin';
import { isNotEmpty } from '@/helpers';
import { ConsoleLogger } from '@/helpers/entities/classes/logger/ConsoleLogger';
import { Bundle } from '@/helpers/entities/enums/v2/Bundle';
import CleverTapEventsMixin from '@/helpers/vue_mixins/v3/CleverTapEventsMixin';
import PromptMutation from '@/plugins/graphql/mutations/v3/prompts/update-product-ids.gql';
let ProductsCreate = class ProductsCreate extends mixins(ProductMixin, ProductImageUploadMixin, ProductVariantsMixin, ProductAddOnMixin, CleverTapEventsMixin) {
    constructor() {
        super(...arguments);
        this.generationUUIDs = [];
    }
    get usesNewForm() {
        return this.bundleKey === Bundle.Ecommerce || this.bundleKey === Bundle.Shopify;
    }
    onExplainerGenerate(uuid) {
        this.generationUUIDs = [...this.generationUUIDs, uuid];
    }
    async onSubmit(form) {
        this.mutationRequestOnFlight();
        await this.create(form)
            .then(async ({ data }) => {
            const productUuid = data.ecommerceProductAdd.product.uuid;
            // Update product IDs for previous generations
            await this.updatePromptProductIds(productUuid);
            if (isNotEmpty(form.images.length)) {
                this.uploadingOnProgress();
                await this.uploadImages(productUuid, form.images);
                this.uploadingDone();
            }
            this.productAction('SUBMIT CREATE');
            const msg = successfulCreate('Product');
            this.successNotification(msg);
            this.goToProductsPage();
            if (!data.ecommerceProductAdd.hasUploadedProduct) {
                this.$store.commit('setTodoSuccessMessage', "Your store is shaping up brilliantly! What's next?");
            }
        })
            .catch((e) => {
            this.errorNotification(e.message);
        })
            .finally(() => {
            this.requestDone();
        });
    }
    newSectionParams(sections) {
        const unDeleted = (sc) => {
            return !sc.isDeleted;
        };
        return sections.filter(unDeleted).map((sc) => {
            return {
                name: sc.name,
                productOptions: sc.buildNewOptionsParams(),
                published: sc.published,
            };
        });
    }
    newDetailsParams(details) {
        const unDeleted = (detail) => {
            return !detail.isDeleted;
        };
        return details.filter(unDeleted).map((detail) => {
            return { content: detail.content };
        });
    }
    async create(form) {
        const appUuid = this.appUUID;
        const productSections = this.newSectionParams(form.productSections);
        const productDetails = this.newDetailsParams(form.productDetails);
        // const productAddOns = () => {
        //   if (form.productAddOns) {
        //     return this.buildProductAddOnParams(form.productAddOns);
        //   }
        //   return null;
        // };
        const productPromo = () => {
            if (form.productPromo) {
                return form.productPromo.buildNewPromoParams();
            }
            return null;
        };
        const categories = this.buildCategoriesParams(form.productCategories);
        const productRegionAvailabilities = this.buildRegionAvailabilityParams(form.productRegionAvailabilities);
        const { name, description, price, quantity, category, limited, enableVariances, enableDependentStock, enablePromos, requiredKyc, kycLevel, enableProductRemarks, remarksLabel, enableProductLocationAvailability, skuMatCode, productVariants, digitalProduct, width, height, length, weight, compareAtPrice, } = form;
        const variants = () => {
            return this.buildProductVariantsParams(productVariants);
        };
        return this.$apollo.mutate({
            mutation: ProductCreate,
            variables: {
                input: {
                    name,
                    description,
                    appUuid,
                    price,
                    category,
                    limited,
                    productSections,
                    enableVariances,
                    enableDependentStock,
                    requiredKyc,
                    kycLevel,
                    productDetails,
                    enablePromo: enablePromos,
                    productPromo: productPromo(),
                    stockQuantity: Number(quantity),
                    categories,
                    enableProductRemarks,
                    remarksLabel,
                    enableProductLocationAvailability,
                    productRegionAvailabilities,
                    skuMatCode,
                    productVariance: variants(),
                    digitalProduct,
                    width: Number(width),
                    height: Number(height),
                    length: Number(length),
                    weight: Number(weight),
                    compareAtPrice: Number(compareAtPrice),
                },
            },
            update(cache, { data }) {
                const typename = 'EcommerceAppProductsEdges';
                const { product } = data.ecommerceProductAdd;
                const queryOptions = {
                    query: Products,
                    variables: {
                        appUuid,
                    },
                };
                const { fetchAppEcommerceProducts } = cache.readQuery({
                    ...queryOptions,
                });
                const productNode = {
                    node: product,
                    __typename: typename,
                };
                if (!fetchAppEcommerceProducts) {
                    return;
                }
                const cachedProducts = {
                    ...fetchAppEcommerceProducts,
                };
                cachedProducts.edges.unshift(productNode);
                try {
                    cache.writeQuery({
                        ...queryOptions,
                        data: {
                            fetchAppEcommerceProducts: cachedProducts,
                        },
                    });
                    // Update Freebies Cache
                    if (product.category === ProductCategory.freebie) {
                        const { fetchAppEcommerceProducts: freebies } = cache.readQuery({
                            ...queryOptions,
                            query: Freebies,
                        });
                        const cachedFreebies = {
                            ...freebies,
                        };
                        cachedFreebies.edges.unshift(productNode);
                        cache.writeQuery({
                            ...queryOptions,
                            query: Freebies,
                            data: {
                                fetchAppEcommerceProducts: cachedFreebies,
                            },
                        });
                    }
                }
                catch (e) {
                    const logger = new ConsoleLogger();
                    logger.info(e.message);
                }
            },
        });
    }
    updatePromptProductIds(productUUID) {
        return this.$apollo
            .mutate({
            mutation: PromptMutation,
            variables: {
                input: {
                    promptUuids: this.generationUUIDs,
                    productUuid: productUUID,
                },
            },
        })
            .catch((e) => {
            this.errorNotification(e.message);
        });
    }
};
ProductsCreate = __decorate([
    Component({
        components: {
            PageTitle,
            ProductForm,
            NewProductForm,
        },
    })
], ProductsCreate);
export default ProductsCreate;
